import { type FC, type MouseEvent, type ReactNode, type Ref } from "react";

import { clsx } from "clsx";

// The assistant's half of a split: the thread (or its empty state) over the
// composer, `chips` right above it. `busy` rests the page tags while the
// assistant answers; `overlay` covers the thread (a page opened under 1280)
// without unmounting it.
export const AssistantColumn: FC<{
  children: ReactNode;
  footer: ReactNode;
  chips?: ReactNode;
  footerDimmed?: boolean;
  busy?: boolean;
  hidden?: boolean;
  // Faded and inert, while the pane beside it is in a mode of its own.
  dimmed?: boolean;
  overlay?: ReactNode;
  bodyRef?: Ref<HTMLDivElement>;
  onClick?(event: MouseEvent<HTMLDivElement>): void;
}> = ({
  children,
  footer,
  chips,
  footerDimmed,
  busy,
  hidden,
  dimmed,
  overlay,
  bodyRef,
  onClick,
}) => (
  <div
    hidden={hidden}
    inert={dimmed}
    className={clsx(
      "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[18px] bg-linear-to-b from-white to-[#fcfdfd] shadow-[inset_0_1px_0_#fff,0_0_0_1px_rgba(109,40,217,.16),0_1px_2px_rgba(18,53,91,.04),0_14px_32px_-14px_rgba(109,40,217,.28)]",
      dimmed && "opacity-50",
    )}
  >
    <div
      ref={bodyRef}
      aria-busy={busy}
      onClick={onClick}
      className="flex min-h-0 flex-1 flex-col"
    >
      {children}
    </div>
    <div className={clsx("px-4 pt-3.5 pb-4", footerDimmed && "opacity-50")}>
      {chips && <div className="mb-2 flex flex-wrap gap-1.5">{chips}</div>}
      {footer}
    </div>
    {overlay && (
      <div className="absolute inset-0 z-10 flex flex-col bg-slate-100">
        {overlay}
      </div>
    )}
  </div>
);

// One of the pane's views, kept mounted while another shows (`hidden`), so
// the thread keeps its place.
export const AssistantColumnView: FC<{
  children: ReactNode;
  hidden?: boolean;
}> = ({ children, hidden }) => (
  <div hidden={hidden} className="flex min-h-0 flex-1 flex-col">
    {children}
  </div>
);

// The empty or locked chat: padded, scrolls on its own.
export const AssistantColumnEmpty: FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-4 text-[12.5px] leading-relaxed text-slate-600">
    {children}
  </div>
);
